import React from 'react';
import PageMeta from '../components/PageMeta';

const sections = [
  {
    title: '1. Information We Collect',
    body: [
      'BTree may collect information you provide directly, including name, email address, account details, subscription selections, payment references and support communications.',
      'We may also collect technical information such as browser type, device information, IP address, pages visited, session activity and cookies or similar identifiers.',
    ],
  },
  {
    title: '2. How We Use Information',
    body: [
      'We use collected information to create and manage accounts, provide subscriptions, process payments, deliver reports and educational content, respond to support requests and improve the platform.',
      'We may use contact information to send service updates, renewal reminders, policy notices and relevant platform communications.',
    ],
  },
  {
    title: '3. Payments',
    body: [
      'Payment processing may be handled by third-party payment providers. BTree does not require users to submit full card or banking credentials directly to BTree systems.',
      'Payment references, transaction status and subscription metadata may be stored to manage access and billing records.',
    ],
  },
  {
    title: '4. Cookies and Analytics',
    body: [
      'BTree may use cookies, local storage and analytics tools to support authentication, remember preferences, measure usage and improve site performance.',
      'Users may adjust browser settings to restrict cookies, though some platform features may not work properly if cookies are disabled.',
    ],
  },
  {
    title: '5. Sharing of Information',
    body: [
      'BTree does not sell personal information.',
      'Information may be shared with service providers that support hosting, authentication, analytics, payment processing, email delivery, customer support or legal compliance.',
    ],
  },
  {
    title: '6. Data Security',
    body: [
      'BTree uses reasonable administrative, technical and organizational safeguards designed to protect user information.',
      'No digital system can be guaranteed to be completely secure, and users should keep account credentials confidential.',
    ],
  },
  {
    title: '7. Data Retention',
    body: [
      'BTree retains information for as long as necessary to provide services, maintain business records, comply with legal obligations, resolve disputes and enforce policies.',
    ],
  },
  {
    title: '8. User Choices',
    body: [
      'Users may request access, correction or deletion of account-related personal information by contacting support, subject to legal, security and operational requirements.',
    ],
  },
  {
    title: '9. Children',
    body: [
      'BTree services are not intended for children under the age required by applicable law to consent to digital services. Users should not create an account if they are not legally permitted to do so.',
    ],
  },
  {
    title: '10. Changes to This Policy',
    body: [
      'BTree reserves the right to modify this Privacy Policy at any time. Updated versions will be published on the website.',
    ],
  },
  {
    title: '11. Contact',
    body: [
      'Privacy questions or requests may be sent to contact@thebtree.com.',
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <>
      <PageMeta
        title="Privacy Policy | The B Tree"
        description="Learn how The B Tree collects, stores, and protects account, subscription, and usage data across the platform."
        canonical="https://www.thebtree.com/privacy"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
      <main className="min-h-screen bg-[#F7F5F2]" data-testid="privacy-page">
      <section className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-18">
          <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
            BTree Policies
          </p>
          <h1 className="text-4xl md:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm md:text-base text-slate-600">
            Effective Date: June 15, 2026
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <p className="text-lg leading-8 text-slate-700 mb-12">
            This Privacy Policy explains how The BTree Company collects, uses, stores and protects information
            when users access the website, subscriptions, reports, educational content and related digital services.
          </p>

          <div className="space-y-10">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="text-2xl md:text-3xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-3">
                  {section.title}
                </h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph} className="text-slate-700 leading-7 mb-4">
                    {paragraph.includes('contact@thebtree.com') ? (
                      <>
                        Privacy questions or requests may be sent to{' '}
                        <a
                          href="mailto:contact@thebtree.com"
                          className="font-medium text-[#9B7A21] hover:text-[#0D1B2A] underline underline-offset-4"
                        >
                          contact@thebtree.com
                        </a>
                        .
                      </>
                    ) : (
                      paragraph
                    )}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </article>
      </section>
    </main>
    </>
  );
};

export default PrivacyPolicy;
